import { format } from "date-fns";
import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { DatePicker as RSDatePicker } from "rsuite";
import { DatoIntervalFilterInput } from "../../../interfaces/schema";
import styles from "../../../styles/components/_c-date-filter.module.scss";
import {
  DateFilterEvent,
  DateFilterSetting,
  DateFilterDateQueries,
  DateFilterRangeQueries,
  DateEvent,
  RangeEvent,
} from "./types";
import { SelectContent, SelectItem, SelectTrigger } from "../../select/Select";
import { Select } from "../../select/Select";
import { ComboBox } from "../ComboBox";
import CloseIcon from "../../icons/CloseIcon";
import { isEqual, random } from "lodash";
import {
  isCompleteDate,
  isTypingEvent,
} from "../../formcontrols/datePickerValue";

// helper const
const dateFormat = "yyyy-MM-dd";

// the function
export default function DateFilter(props: {
  handleChange: (dateFilterEvent: DateFilterEvent) => void;
  settings: DateFilterSetting;
  disabled?: boolean;
}) {
  // props and destructs
  const { handleChange, settings, disabled } = props;

  // keep track of today for each render to keep "no values" in sync
  const today = useMemo(() => {
    return new Date();
  }, []);

  // unwrap settings
  const settingsFilterType = settings?.filterType || "today";

  // settings for date filter
  const dateFilterDate = useMemo(() => {
    return settings?.filterType === "date"
      ? new Date(settings.filterSettings.date)
      : today;
  }, [settings, today]);

  const dateFilterQuery =
    settings?.filterType === "date"
      ? settings.filterSettings.query
      : DateFilterDateQueries[0];

  // settings for advanced filter
  // start settings
  const advancedFilterStartQuery =
    settings?.filterType === "advanced"
      ? settings.filterSettings.start.query
      : DateFilterRangeQueries[1];

  const advancedFilterStartFromDate =
    settings?.filterType === "advanced"
      ? new Date(settings.filterSettings.start.range.from)
      : today;

  const advancedFilterStartToDate =
    settings?.filterType === "advanced"
      ? new Date(settings.filterSettings.start.range.to)
      : today;

  // end settings
  const advancedFilterEndQuery =
    settings?.filterType === "advanced"
      ? settings.filterSettings.end.query
      : DateFilterRangeQueries[2];

  const advancedFilterEndFromDate =
    settings?.filterType === "advanced"
      ? new Date(settings.filterSettings.end.range.from)
      : today;

  const advancedFilterEndToDate =
    settings?.filterType === "advanced"
      ? new Date(settings.filterSettings.end.range.to)
      : today;

  // state for id's
  const [btnGroupId, setBtnGroupId] = useState("");
  const [todayId, setTodayId] = useState("");
  const [dateId, setDateId] = useState("");
  const [advancedId, setAdvancedId] = useState("");

  // state - for filter
  const [selectedFilter, setSelectedFilter] = useState(settingsFilterType);

  // state - for date
  const [dateQuery, setDateQuery] = useState(dateFilterQuery);
  const [selectedDate, setSelectedDate] = useState(dateFilterDate);

  // state - for advanced
  const [advancedStartQuery, setAdvancedStartQuery] = useState(
    advancedFilterStartQuery,
  );
  const [advancedStartFromDate, setAdvancedStartFromDate] = useState(
    advancedFilterStartFromDate,
  );
  const [advancedStartToDate, setAdvancedStartToDate] = useState(
    advancedFilterStartToDate,
  );

  const [advancedEndQuery, setAdvancedEndQuery] = useState(
    advancedFilterEndQuery,
  );
  const [advancedEndFromDate, setAdvancedEndFromDate] = useState(
    advancedFilterEndFromDate,
  );
  const [advancedEndToDate, setAdvancedEndToDate] = useState(
    advancedFilterEndToDate,
  );

  // What is typed in each picker, shown until it is applied. rsuite 6 calls
  // onChange on every keystroke, but a typed date is only applied on Enter or
  // when the field is left, as with rsuite 5: every applied date changes the
  // key the parent filters give this component, and the remount drops the
  // focus mid-typing (ADV-320). A click in the calendar applies at once.
  const [drafts, setDrafts] = useState<Record<string, Date | null>>({});
  function pickerProps(
    key: string,
    value: Date,
    apply: (finished: Date) => void,
  ) {
    function finishTyping(dropUnfinished: boolean) {
      const draft = drafts[key];
      if (!draft) return;
      if (isCompleteDate(draft)) {
        setDrafts((current) => ({ ...current, [key]: null }));
        apply(draft);
      } else if (dropUnfinished) {
        setDrafts((current) => ({ ...current, [key]: null }));
      }
    }
    return {
      value: drafts[key] ?? value,
      onChange: (date: Date | null, event?: SyntheticEvent) => {
        if (isTypingEvent(event)) {
          setDrafts((current) => ({ ...current, [key]: date }));
          return;
        }
        setDrafts((current) => ({ ...current, [key]: null }));
        if (isCompleteDate(date)) apply(date);
      },
      onKeyDown: (event: { key: string }) => {
        if (event.key === "Enter") finishTyping(false);
      },
      onBlurCapture: () => finishTyping(true),
    };
  }

  // memos for "default" values
  const hasDateValue = useMemo(() => {
    if (dateFilterDate.toDateString() === today.toDateString()) {
      if (isEqual(dateQuery, DateFilterDateQueries[0])) {
        return false;
      }
    }

    return true;
  }, [dateFilterDate, dateQuery, today]);

  const hasAdvancedStartValue = useMemo(() => {
    if (
      advancedStartFromDate.toDateString() === today.toDateString() &&
      advancedStartToDate.toDateString() === today.toDateString()
    ) {
      if (isEqual(advancedStartQuery, DateFilterRangeQueries[1])) {
        return false;
      }
    }

    return true;
  }, [advancedStartFromDate, advancedStartQuery, advancedStartToDate, today]);

  const hasAdvancedEndValue = useMemo(() => {
    if (
      advancedEndFromDate.toDateString() === today.toDateString() &&
      advancedEndToDate.toDateString() === today.toDateString()
    ) {
      if (isEqual(advancedEndQuery, DateFilterRangeQueries[2])) {
        return false;
      }
    }

    return true;
  }, [advancedEndFromDate, advancedEndQuery, advancedEndToDate, today]);

  // handlers
  function dateEventHandler(dateEvent: DateEvent) {
    const query = dateEvent?.query || dateQuery;
    const date = dateEvent?.date || selectedDate;

    let filter: DatoIntervalFilterInput;

    switch (query.type) {
      case "eq":
        filter = {
          and: [
            {
              fraDato: {
                lte: format(date, dateFormat),
              },
            },
            {
              tilDatoInklusiv: {
                gte: format(date, dateFormat),
              },
            },
          ],
        };
        break;
    }

    handleChange({
      event: "date",
      filter: filter,
      settings: {
        filterType: "date",
        filterSettings: {
          date: date,
          query: query,
        },
      },
    });
  }

  function clearDateHandler() {
    dateEventHandler({
      date: today,
      query: DateFilterDateQueries[0],
    });
    setDateQuery(DateFilterDateQueries[0]);
    setSelectedDate(today);
    setDrafts((current) => ({ ...current, date: null }));
  }

  function rangeEventHandler(rangeEvent: RangeEvent) {
    // get selected queries
    const startQuery = rangeEvent?.start?.query || advancedStartQuery;
    const endQuery = rangeEvent.end?.query || advancedEndQuery;

    // get the available dates
    const startFromDate =
      rangeEvent.start?.range?.from || advancedStartFromDate;
    const startToDate = rangeEvent.start?.range?.to || advancedStartToDate;

    const endFromDate = rangeEvent.end?.range?.from || advancedEndFromDate;
    const endToDate = rangeEvent.end?.range?.to || advancedEndToDate;

    // build complicated from filter
    let fromFilter: DatoIntervalFilterInput | null = null;

    switch (startQuery.type) {
      case "eq":
        fromFilter = {
          and: [
            { fraDato: { gte: format(startFromDate, dateFormat) } },
            { fraDato: { lte: format(startToDate, dateFormat) } },
          ],
        };
        break;
      case "lte":
        fromFilter = {
          and: [
            { fraDato: { lte: format(startFromDate, dateFormat) } },
            // { fraDato: { lte: format(startToDate, dateFormat) } },
          ],
        };
        break;
      case "gt":
        fromFilter = {
          and: [
            // { fraDato: { gte: format(startFromDate, dateFormat) } },
            { fraDato: { gte: format(startToDate, dateFormat) } },
          ],
        };
        break;
    }

    // construct the complicated to filter
    let toFilter: DatoIntervalFilterInput | null = null;
    switch (endQuery.type) {
      case "eq":
        toFilter = {
          and: [
            { tilDatoInklusiv: { gte: format(endFromDate, dateFormat) } },
            { tilDatoInklusiv: { lte: format(endToDate, dateFormat) } },
          ],
        };
        break;
      case "lte":
        toFilter = {
          and: [{ tilDatoInklusiv: { lte: format(endFromDate, dateFormat) } }],
        };
        break;
      case "gt":
        toFilter = {
          and: [{ tilDatoInklusiv: { gte: format(endToDate, dateFormat) } }],
        };
        break;
    }

    const filterArray: DatoIntervalFilterInput[] = [];

    if (fromFilter) {
      filterArray.push(fromFilter);
    }

    if (toFilter) {
      filterArray.push(toFilter);
    }

    let filter: DatoIntervalFilterInput = {};

    if (filterArray.length) {
      filter = {
        and: filterArray,
      };
    }

    handleChange({
      event: "advanced",
      filter: filter,
      settings: {
        filterType: "advanced",
        filterSettings: {
          start: {
            query: startQuery,
            range: {
              from: startFromDate,
              to: startToDate,
            },
          },
          end: {
            query: endQuery,
            range: {
              from: endFromDate,
              to: endToDate,
            },
          },
        },
      },
    });
  }

  function clearStartRangeHandler() {
    rangeEventHandler({
      start: {
        query: DateFilterRangeQueries[1],
        range: {
          from: today,
          to: today,
        },
      },
    });
    setAdvancedStartQuery(DateFilterRangeQueries[1]);
    setAdvancedStartFromDate(today);
    setAdvancedStartToDate(today);
    setDrafts((current) => ({ ...current, startFrom: null, startTo: null }));
  }

  function clearEndRangeHandler() {
    rangeEventHandler({
      end: {
        query: DateFilterRangeQueries[2],
        range: {
          from: today,
          to: today,
        },
      },
    });
    setAdvancedEndQuery(DateFilterRangeQueries[2]);
    setAdvancedEndFromDate(today);
    setAdvancedEndToDate(today);
    setDrafts((current) => ({ ...current, endFrom: null, endTo: null }));
  }

  // effects
  useEffect(() => {
    setBtnGroupId(`grp-${String(random(0, 100000))}`);
    setTodayId(String(random(0, 100000)));
    setDateId(String(random(0, 100000)));
    setAdvancedId(String(random(0, 100000)));
  }, []);

  return (
    <div className={`${styles.wrapper}`}>
      <h4 className="h6 mb-1 text-secondary">Periode</h4>
      <div className={`${styles.header}`}>
        <input
          checked={selectedFilter === "today"}
          className={`btn-secondary ${styles.radio}`}
          disabled={disabled}
          id={todayId}
          name={btnGroupId}
          type="radio"
          onChange={() => {
            setSelectedFilter("today");
            setDrafts({});
            const nowDate = new Date();
            handleChange({
              event: "date",
              filter: {
                and: [
                  {
                    fraDato: { lte: format(nowDate, dateFormat) },
                  },
                  {
                    tilDatoInklusiv: { gte: format(nowDate, dateFormat) },
                  },
                ],
              },
              settings: {
                filterType: "today",
              },
            });
          }}
        />
        <label htmlFor={todayId} className={`${styles.label}`}>
          I dag
        </label>

        <input
          checked={selectedFilter === "date"}
          className={`btn-secondary ${styles.radio}`}
          disabled={disabled}
          id={dateId}
          name={btnGroupId}
          type="radio"
          onChange={() => {
            setSelectedFilter("date");
            setDrafts({});
            dateEventHandler({});
          }}
        />
        <label htmlFor={dateId} className={`${styles.label}`}>
          Dato
        </label>

        <input
          checked={selectedFilter === "advanced"}
          className={`btn-secondary ${styles.radio}`}
          disabled={disabled}
          id={advancedId}
          name={btnGroupId}
          type="radio"
          onChange={() => {
            setSelectedFilter("advanced");
            setDrafts({});
            rangeEventHandler({});
          }}
        />
        <label htmlFor={advancedId} className={`${styles.label}`}>
          Avanceret
        </label>
      </div>
      <div>
        {(() => {
          switch (selectedFilter) {
            case "today":
              return <></>;
            case "date":
              return (
                <>
                  <ComboBox className="mt-2" hasValue={hasDateValue}>
                    <Select
                      // disabled={disabled}
                      disabled
                      value={DateFilterDateQueries.findIndex(
                        (dq) => dq.type === dateQuery.type,
                      ).toString()}
                      onValueChange={(value: string) => {
                        dateEventHandler({
                          query: DateFilterDateQueries[value],
                        });
                        setDateQuery(DateFilterDateQueries[value]);
                      }}
                    >
                      <SelectTrigger
                        aria-label="Vælg datotype"
                        className={`${styles.select}`}
                        badge
                      />
                      <SelectContent small>
                        {DateFilterDateQueries.map((dq, index) => {
                          return (
                            <SelectItem key={index} value={index.toString()}>
                              {dq.label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <RSDatePicker
                      appearance="subtle"
                      cleanable={false}
                      disabled={disabled}
                      format="dd.MM.yyyy"
                      oneTap
                      size="xs"
                      {...pickerProps("date", selectedDate, (finished) => {
                        dateEventHandler({ date: finished });
                        setSelectedDate(finished);
                      })}
                      placement="autoVerticalStart"
                    />
                    {hasDateValue && (
                      <button
                        aria-label="Nulstil"
                        className={`btn btn-secondary btn--transparent btn--icon ${styles.reset_btn}`}
                        disabled={!hasDateValue || disabled}
                        onClick={clearDateHandler}
                      >
                        <CloseIcon height={16} width={16} />
                      </button>
                    )}
                  </ComboBox>
                </>
              );
            case "advanced":
              return (
                <>
                  <div className="mt-1">
                    <label className="form-label">Startdato</label>
                    <ComboBox hasValue={hasAdvancedStartValue}>
                      <Select
                        disabled={disabled}
                        value={DateFilterRangeQueries.findIndex(
                          (rq) => rq.type === advancedStartQuery.type,
                        ).toString()}
                        onValueChange={(value: string) => {
                          rangeEventHandler({
                            start: {
                              query: DateFilterRangeQueries[value],
                            },
                          });
                          setAdvancedStartQuery(DateFilterRangeQueries[value]);
                        }}
                      >
                        <SelectTrigger
                          aria-label="Vælg datotype"
                          className={`${styles.select}`}
                          badge
                        />
                        <SelectContent small>
                          {DateFilterRangeQueries.map((rq, index) => {
                            return (
                              <SelectItem key={index} value={index.toString()}>
                                {rq.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {advancedStartQuery.type !== "gt" && (
                        <RSDatePicker
                          appearance="subtle"
                          className={`${styles.datepicker}`}
                          cleanable={false}
                          disabled={disabled}
                          format="dd.MM.yyyy"
                          oneTap
                          size="xs"
                          {...pickerProps(
                            "startFrom",
                            advancedStartFromDate,
                            (finished) => {
                              rangeEventHandler({
                                start: { range: { from: finished } },
                              });
                              setAdvancedStartFromDate(finished);
                            },
                          )}
                          placement="autoVerticalStart"
                        />
                      )}

                      {advancedStartQuery.type === "eq" && <>-</>}

                      {advancedStartQuery.type !== "lte" && (
                        <RSDatePicker
                          appearance="subtle"
                          className={`${styles.datepicker}`}
                          cleanable={false}
                          disabled={disabled}
                          format="dd.MM.yyyy"
                          oneTap
                          size="xs"
                          {...pickerProps(
                            "startTo",
                            advancedStartToDate,
                            (finished) => {
                              rangeEventHandler({
                                start: { range: { to: finished } },
                              });
                              setAdvancedStartToDate(finished);
                            },
                          )}
                          placement="autoVerticalStart"
                        />
                      )}
                      {hasAdvancedStartValue && (
                        <button
                          aria-label="Nulstil"
                          className={`btn btn-secondary btn--transparent btn--icon flex-shrink-0 w-auto ${styles.reset_btn}`}
                          disabled={!hasAdvancedStartValue || disabled}
                          onClick={clearStartRangeHandler}
                        >
                          <CloseIcon height={16} width={16} />
                        </button>
                      )}
                    </ComboBox>
                  </div>
                  <div className="mt-2">
                    <label className="form-label">Slutdato</label>
                    <ComboBox hasValue={hasAdvancedEndValue}>
                      <Select
                        disabled={disabled}
                        value={DateFilterRangeQueries.findIndex(
                          (rq) => rq.type === advancedEndQuery.type,
                        ).toString()}
                        onValueChange={(value: string) => {
                          rangeEventHandler({
                            end: {
                              query: DateFilterRangeQueries[value],
                            },
                          });
                          setAdvancedEndQuery(DateFilterRangeQueries[value]);
                        }}
                      >
                        <SelectTrigger
                          aria-label="Vælg datotype"
                          className={`${styles.select}`}
                          badge
                        />
                        <SelectContent small>
                          {DateFilterRangeQueries.map((rq, index) => {
                            return (
                              <SelectItem key={index} value={index.toString()}>
                                {rq.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {advancedEndQuery.type !== "gt" && (
                        <RSDatePicker
                          appearance="subtle"
                          className={`${styles.datepicker}`}
                          cleanable={false}
                          disabled={disabled}
                          format="dd.MM.yyyy"
                          oneTap
                          size="xs"
                          {...pickerProps(
                            "endFrom",
                            advancedEndFromDate,
                            (finished) => {
                              rangeEventHandler({
                                end: { range: { from: finished } },
                              });
                              setAdvancedEndFromDate(finished);
                            },
                          )}
                          placement="autoVerticalStart"
                        />
                      )}

                      {advancedEndQuery.type === "eq" && <>-</>}

                      {advancedEndQuery.type !== "lte" && (
                        <RSDatePicker
                          appearance="subtle"
                          className={`${styles.datepicker}`}
                          cleanable={false}
                          disabled={disabled}
                          format="dd.MM.yyyy"
                          oneTap
                          size="xs"
                          {...pickerProps(
                            "endTo",
                            advancedEndToDate,
                            (finished) => {
                              rangeEventHandler({
                                end: { range: { to: finished } },
                              });
                              setAdvancedEndToDate(finished);
                            },
                          )}
                          placement="autoVerticalStart"
                        />
                      )}
                      {hasAdvancedEndValue && (
                        <button
                          aria-label="Nulstil"
                          className={`btn btn-secondary btn--transparent btn--icon flex-shrink-0 w-auto ${styles.reset_btn}`}
                          disabled={!hasAdvancedEndValue || disabled}
                          onClick={clearEndRangeHandler}
                        >
                          <CloseIcon height={16} width={16} />
                        </button>
                      )}
                    </ComboBox>
                  </div>
                </>
              );
          }
        })()}
      </div>
    </div>
  );
}
